import { consumeAuthDenialMessage, getAuthDenialCode } from '@Core/Auth/authDenial'
import { LoginProvider, loginErrorMessage } from '@Core/Auth/loginError'
import {
    logoutAction,
    setIsAuthenticationFinishedAction,
    setIsLoggedAction,
    setIsSessionExpiredAction,
} from '@Core/Communication/GlobalStore/Reducers/authentication/reducer'
import { API } from '@Services/api'
import { isTokenExpired } from '@Utils/Support/expiredJWT'
import {
    OAuthProvider,
    User,
    getAuth,
    signInWithEmailAndPassword,
    signInWithPopup,
    signOut,
} from 'firebase/auth'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export const ACCESS_TOKEN_KEY = '@ofc:accessToken'
export const MS_ACCESS_TOKEN_KEY = '@ofc:msAccessToken'
export const LAST_PROVIDER_KEY = '@ofc:lastProvider'
export const LAST_UID_KEY = '@ofc:lastUid'

export type { LoginProvider }
export { loginErrorMessage }

interface AuthStorage {
    getItem(key: string): string | null
    setItem(key: string, value: string): void
    removeItem?(key: string): void
}

export const useAuth = (
    storage: AuthStorage,
    provider: OAuthProvider,
    sessionRenew: boolean = false
) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)

    const tenant = import.meta.env.VITE_MS_TENANT_ID
    provider.setCustomParameters({
        ...(tenant ? { tenant } : {}),
        prompt: 'select_account',
    })

    /**
     * Stores the Firebase ID token (what every API accepts) and asks the API
     * whether this account may enter before showing anything.
     */
    const completeSignIn = async (user: User, loginProvider: LoginProvider) => {
        storage.setItem(ACCESS_TOKEN_KEY, await user.getIdToken())
        storage.setItem(LAST_PROVIDER_KEY, loginProvider)
        const previousUid = storage.getItem(LAST_UID_KEY)
        storage.setItem(LAST_UID_KEY, user.uid)
        await API.get('/user/me/permissions')
        // Another account signed in over the expired session: the screens
        // underneath hold the previous user's data, so start over clean.
        if (sessionRenew && previousUid && previousUid !== user.uid) {
            toast.info('Você entrou com outra conta. Recarregando...')
            window.location.assign('/')
            return
        }
        dispatch(setIsAuthenticationFinishedAction(true))
        dispatch(setIsLoggedAction(true))
        dispatch(setIsSessionExpiredAction(false))
        if (!sessionRenew) navigate('/')
    }

    const failSignIn = async (error: unknown, loginProvider: LoginProvider) => {
        console.error(error)
        // The API interceptor already ended the session for denials; drop the
        // copy it kept for the login screen, the message is shown right here.
        if (getAuthDenialCode(error)) consumeAuthDenialMessage()
        else if ((error as { response?: unknown })?.response) {
            await signOut(getAuth()).catch(() => undefined)
        }
        const message = loginErrorMessage(error, loginProvider)
        if (message) toast.error(message)
    }

    const authenticate = async () => {
        setLoading(true)
        try {
            const result = await signInWithPopup(getAuth(), provider)
            const credential = OAuthProvider.credentialFromResult(result)
            if (credential?.accessToken) {
                storage.setItem(MS_ACCESS_TOKEN_KEY, credential.accessToken)
            }
            await completeSignIn(result.user, 'microsoft.com')
        } catch (error) {
            await failSignIn(error, 'microsoft.com')
        } finally {
            setLoading(false)
        }
    }

    const uiAuthenticate = async (username: string, password: string) => {
        setLoading(true)
        try {
            const result = await signInWithEmailAndPassword(
                getAuth(),
                username,
                password
            )
            await completeSignIn(result.user, 'password')
        } catch (error) {
            await failSignIn(error, 'password')
        } finally {
            setLoading(false)
        }
    }

    const getIsAuthenticated = async () => {
        if (!storage.getItem(ACCESS_TOKEN_KEY)) return
        if (isTokenExpired(String(storage.getItem(ACCESS_TOKEN_KEY)))) {
            dispatch(logoutAction())
            return
        }
        dispatch(setIsAuthenticationFinishedAction(true))
        dispatch(setIsLoggedAction(true))
        dispatch(setIsSessionExpiredAction(false))
        if (!sessionRenew) {
            navigate('/')
        }
    }

    useEffect(() => {
        getIsAuthenticated()
    }, [])

    return {
        authenticate,
        uiAuthenticate,
        loading,
    }
}
